import React, { useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';

import Label from './Label';
import ValidationError from './ValidationError';
import { colors } from '../styles/colors';

// Mixins

const errorInputMixin = css`
  border-bottom: 1px solid ${colors.DANGER};
  
  &:focus {
    border-bottom: 1px solid ${colors.DANGER};
  }
`;

// Components

export const InputContainer = styled.div`
  border-bottom: 1px solid ${colors.GRAY};
  box-sizing: border-box;
  overflow: hidden;
  background: white;
  border-color: 2px solid transparent;
  
  ${({ isFocused }) => isFocused && css`
    border-color: ${colors.BRAND_PRIMARY};
  `}
  
  ${({ meta }) => meta && meta.touched && meta.error ? errorInputMixin : ''}
`;


export const Input = styled.input`
  min-width: 0;
  height: 40px;
  border: 0;
  background: white;
  appearance: none;
  font-size: 12px;
  width: 100%;
  color: ${colors.BRAND_SECONDARY};
  outline: none;
  box-sizing: border-box;
  text-align: ${({ alignCenter }) => alignCenter ? 'center' : 'initial'};

  ::placeholder {
    color: ${colors.GRAY};
  }

  &[disabled] {
    background-color: ${colors.GRAY_LIGHTEST};
    color: ${colors.GRAY_DARKER}
  }
`;


const InputField = ({
  type,
  placeholder,
  disabled,
  label,
  field,
  format,
  parse,
  isRequired,
  classname,
  fieldProps,
  form: {
    setFieldTouched,
    touched,
    errors,
  },
  inputProps,
  alignCenter,
  onInputRef,
  onChange,
  onBlur,
  onPressEnter,
  errorMessage,
  ...props
}) => {
  const inputRef = useRef(null);
  const selectionStart = useRef(null);
  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    if (inputRef && inputRef.current && inputRef.current.selectionStart && (selectionStart.current || selectionStart.current === 0)) {
      inputRef.current.selectionStart = selectionStart.current;
      inputRef.current.selectionEnd = selectionStart.current;
    }
  }, []);

  const meta = {
    touched: touched[field.name],
    error: errors[field.name] ? errorMessage : '',
    isFocused,
  };

  const _onFocus = () => {
    setIsFocused(true);
  };

  const _onBlur = () => {
    setIsFocused(false);
  };

  return (
    <div {...props}>
      {label && (
        <Label
          meta={meta}
          isRequired={isRequired}
        >
          {label}
        </Label>
      )}
      <InputContainer
        meta={meta}
        isFocused={meta.isFocused}
      >
        <Input
          ref={(ref) => {
            inputRef.current = ref;
            if (onInputRef) onInputRef(ref);
          }}
          type={type}
          placeholder={placeholder}
          disabled={disabled}
          {...field}
          onKeyDown={(e) => {
            if (e.key.toLowerCase() === 'enter') {
              onPressEnter(field.value);
            }
          }}
          onFocus={() => {
            _onFocus();
          }}
          onBlur={() => {
            onBlur(field.value);
            _onBlur();

            if (!meta.touched) {
              setFieldTouched(field.name, true);
            }
          }}
          value={format(field.value)}
          alignCenter={alignCenter}
          onChange={(e) => {
            let currentSelection = e.target.selectionStart;
            const event = { ...e };
            const origValue = e.target.value;
            const maskedPrevValue = format(field.value) || '';

            // Note - we call format function to clear the input value
            // to remove exceeded characters
            event.target.value = parse(format(origValue));
            const maskedValue = format(event.target.value);

            const diffCaretPosistion = maskedValue.length - maskedPrevValue.length;
            currentSelection += (diffCaretPosistion > 1 ? diffCaretPosistion : 0);

            field.onChange(event);

            onChange(event.target.value);
            selectionStart.current = currentSelection;
          }}
          {...fieldProps}
        />
      </InputContainer>
      <ValidationError
        meta={meta}
      />
    </div>
  );
};

export default InputField;
